import clsx from 'clsx'
import { format } from 'date-fns'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'

import GraphLoadingImage from '@/assets/graph-loading.svg?react'
import { Card, createAreaSeriesOptions, createLineSeriesOptions } from '@/components/core-v2'
import { CURRENCY } from '@/const'
import { formatCurrency, formatCurrencyFull } from '@/utilities/currency'

import { useFilteredGetByTenantAccountDashboardQuery } from '../../utils/useFilteredNotes'
import classes from './index.module.scss'

type TimeSeries = [number, number][]

const options = (totalValueSeries: TimeSeries, netContributionsSeries: TimeSeries) => ({
  chart: {
    type: 'area',
    height: 500,
    backgroundColor: 'none',
    borderColor: 'none',
    borderWidth: 0,
    zooming: {
      type: 'x'
    }
  },
  title: {
    text: undefined
  },
  xAxis: {
    labels: {
      y: 32
    },
    dateTimeLabelFormats: {
      day: '%e %B',
      week: '%e %B',
      month: '%B %Y',
      year: '%Y'
    },
    className: 'xaxis-label',
    tickPixelInterval: 200,
    title: null,
    type: 'datetime',
    minPadding: 0
  },
  yAxis: [
    {
      labels: {
        align: 'left',
        y: 4,
        x: 0,
        formatter() {
          return `<span class='yaxis-label'>${
            this.value ? formatCurrency(Number(this.value), CURRENCY) : 0
          }</span>`
        }
      },
      min: 0,
      minPadding: 500,
      offset: 10,
      opposite: true,
      title: null,
      tickLength: 40
    }
  ],
  scrollbar: {
    enabled: false
  },
  legend: {
    enabled: true,
    align: 'left',
    x: 0,
    margin: 24,
    itemStyle: {
      fontSize: '16px',
      fontWeight: '400',
      cursor: 'pointer',
      lineHeight: '16px',
      transform: 'translateY(-2px)'
    }
  },
  tooltip: {
    formatter() {
      const { series, x: timestamp, y: value } = this

      return `<div>
          <p>${format(new Date(timestamp), 'EEEE d MMMM yyyy')}</p>
          <p>${series.name}: ${formatCurrencyFull(value, CURRENCY)}</p>
        </div>`
    },
    className: 'tooltip',
    enabled: true,
    animation: false,
    backgroundColor: 'var(--White)',
    borderColor: 'var(--White20)',
    borderRadius: 8,
    borderWidth: 1.5,
    padding: 12,
    shape: 'callout',
    shadow: false,
    useHTML: true,
    distance: 32,
    style: {
      fontSize: '15'
    }
  },
  series: [
    createAreaSeriesOptions(draft => {
      draft.data = totalValueSeries
      draft.name = 'Total value'
      draft.animation = false
      draft.type = 'area'
      draft.legendIndex = 1
      draft.color = 'var(--Crimson-Light)'
      draft.fillColor = {
        linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
        stops: [
          [0, 'var(--Crimson-Light)'],
          [1, '#df5a66']
        ]
      }
    }),
    createLineSeriesOptions(draft => {
      draft.data = netContributionsSeries
      draft.color = 'var(--White20)'
      draft.name = 'Net contributions'
      draft.animation = false
      draft.legendIndex = 2
    })
  ]
})

export const TotalAccountValueChart = () => {
  const { data, isFetching, isLoading } = useFilteredGetByTenantAccountDashboardQuery()

  if (isLoading || isFetching) {
    return (
      <Card.Wrapper className={clsx(classes.skeleton, 'pulse')}>
        <GraphLoadingImage />
      </Card.Wrapper>
    )
  }

  const history = data.aggregateAccountHistory
  const totalValueSeries: TimeSeries = history.dates.map((date, index) => [
    new Date(date).getTime(),
    history.totalValues[index]
  ])
  const netContributionsSeries: TimeSeries = history.dates.map((date, index) => [
    new Date(date).getTime(),
    history.netContributions[index]
  ])

  return (
    <Card.Wrapper className={classes.chartWrapper}>
      <HighchartsReact
        highcharts={Highcharts}
        options={options(totalValueSeries, netContributionsSeries)}
      />
    </Card.Wrapper>
  )
}
